import type { ReactNode } from 'react';
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  InboxOutlined,
  PrinterOutlined,
  RollbackOutlined,
  SendOutlined,
  StopOutlined,
  TagOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { OrderStatusGroup, ShippingPartnerName } from '@tcr-erp/shared';
import type { ActionTone } from '@/components/shared/actionMenuStyle';
import type { TenantOrder } from '@/mocks/orders';
import {
  canCancelOrder,
  canReturnOrder,
  isOrderLockedForUser,
  ORDER_STATUS_LOCK_TOOLTIP,
} from '../utils/orderHeaderActions';
import { PRINT_SLIP_STATUS_GROUPS, resolvePrintSlipMode } from '../utils/orderPrintSlip';
import { FULFILLMENT_ACTION_ROLES } from '../utils/orderActionRoles';
import { excludedReasons, isTerminalOrder, type BatchActionPlan, type BatchOrderPlans } from '../utils/batchOrderActions';
import type { RowMenuExtraPlans } from '../utils/rowMenuBatch';
import { GT_CONFIRM_DENIED_TOOLTIP, isGtPrepaidOrder } from '../components/confirmOrderPrompt';

/**
 * TCR-2504 — trang Đơn hàng chỉ còn MỘT menu hành động: panel "Hành động cho N đơn hàng" neo ở
 * tiêu đề cột Mã đơn. Menu ⋮ từng dòng (TCR-1094/1238/2264) bị bỏ; mọi mục của nó chuyển vào
 * panel, trừ Xem chi tiết / Sửa đơn (đã có trong popup mở khi bấm mã đơn). File này chỉ lập
 * danh sách nút — side-effect nằm ở `Orders/index.tsx`.
 *
 * - Chọn 1 đơn: giữ nguyên luật của menu ⋮ cũ cho dòng đó (nút nào hiện, khi nào khoá, tooltip
 *   lý do) — luồng từng đơn có những cảnh báo mà luồng hàng loạt không thay được (GT trả trước,
 *   vượt hạn mức, khoá theo trạng thái).
 * - Chọn từ 2 đơn: nút chuyển trạng thái hiện khi có ≥1 đơn ở trạng thái đó (chọn lẫn trạng thái
 *   vẫn được); số trong ngoặc là số đơn ĐỦ ĐIỀU KIỆN theo `planBatchOrderActions` /
 *   `planRowMenuExtras`, bấm chỉ xử lý các đơn đó. Hành động chỉ làm được cho 1 đơn vẫn hiện nhưng
 *   mờ kèm lý do.
 *
 * Quyền: 3 nút in ẩn hẳn khi thiếu quyền (TCR-1710 — tránh trùng nghĩa với nút mờ "chưa tới lúc
 * in"), "Đang đóng gói"/"Đã gửi hàng" ẩn với role không phải owner/manager (BE chặn bằng
 * RolesGuard). Các nút còn lại vẫn hiện và báo thiếu quyền khi bấm (TCR-1709/1714).
 */

export const SINGLE_ORDER_ONLY = 'Chỉ áp dụng khi chọn 1 đơn';

export interface OrderPanelEntry {
  key: string;
  text: string;
  tone: ActionTone;
  icon: ReactNode;
  /** Số đơn hiển thị trong ngoặc; vắng = hành động không đếm (Sao chép, In phiếu…). */
  count?: number;
  disabled: boolean;
  tooltip?: string;
}

export interface OrderPanelSections {
  transition: OrderPanelEntry[];
  action: OrderPanelEntry[];
}

/**
 * TCR-1710 — 3 quyền con thay cho key gộp `orders.print`, truyền vào dạng boolean đã resolve để
 * hàm lập danh sách giữ tính thuần (test trực tiếp, không cần dựng `useRbac`).
 */
export interface OrderPrintPermissions {
  canPrintSlip: boolean;
  canPrintReceipt: boolean;
  canPrintInvoice: boolean;
}

export interface OrderPanelContext {
  roles: readonly string[];
  printPermissions: OrderPrintPermissions;
  /** TCR-1964 — quyền `orders.override_lock`; `true` = không bị khoá sửa/huỷ theo trạng thái. */
  canOverrideLock: boolean;
  /** TCR-2130 — quyền `orders.confirm_gt_prepaid`. */
  canConfirmGtPrepaid: boolean;
}

const ICONS = {
  submitDraft: <SendOutlined />,
  deleteDraft: <DeleteOutlined />,
  confirm: <CheckCircleOutlined />,
  advancePacking: <InboxOutlined />,
  markPickedUp: <CarOutlined />,
  cancel: <CloseOutlined />,
  copy: <CopyOutlined />,
  return: <RollbackOutlined />,
  print: <PrinterOutlined />,
  send: <SendOutlined />,
  tags: <TagOutlined />,
  skip: <StopOutlined />,
  unskip: <UndoOutlined />,
};

function entry(
  key: string,
  text: string,
  tone: ActionTone,
  icon: ReactNode,
  { count, disabled = false, tooltip }: { count?: number; disabled?: boolean; tooltip?: string } = {},
): OrderPanelEntry {
  return { key, text, tone, icon, count, disabled, tooltip };
}

/** Mục đếm theo kế hoạch hàng loạt: mờ + lý do khi không đơn nào đủ điều kiện. */
function planned(key: string, text: string, tone: ActionTone, icon: ReactNode, plan: BatchActionPlan): OrderPanelEntry {
  return entry(key, text, tone, icon, {
    count: plan.eligible.length,
    disabled: plan.eligible.length === 0,
    tooltip: excludedReasons(plan),
  });
}

const canFulfill = (roles: readonly string[]) => FULFILLMENT_ACTION_ROLES.some((role) => roles.includes(role));

/** Panel khi chọn đúng 1 đơn — cùng luật menu ⋮ cũ của dòng đó, nhãn đếm "(1)". */
export function buildSingleOrderSections(row: TenantOrder, ctx: OrderPanelContext): OrderPanelSections {
  const group = row.status_group;
  const one = { count: 1 };
  const transition: OrderPanelEntry[] = [];

  // TCR-1963 — đơn Nháp: "Gửi xác nhận" (DRAFT→PENDING) và "Xoá hẳn" (nhả tồn + xoá bản ghi).
  if (group === OrderStatusGroup.DRAFT) {
    transition.push(entry('submit-draft', 'Gửi xác nhận', 'primary', ICONS.submitDraft, one));
    transition.push(entry('delete-draft', 'Xoá hẳn', 'danger', ICONS.deleteDraft, one));
  }
  // TCR-2129 — đơn đã bị trả lại để sửa CK thì ẩn "Xác nhận" (BE cũng chặn 400).
  if (group === OrderStatusGroup.AWAITING_CONFIRMATION && !row.discount_review?.returned) {
    // TCR-2130 — đơn kênh GT chỉ người có quyền GT duyệt được: vẫn hiện nhưng mờ + lý do.
    const gtDenied = isGtPrepaidOrder(row) && !ctx.canConfirmGtPrepaid;
    transition.push(entry('confirm', 'Xác nhận', 'primary', ICONS.confirm, {
      ...one,
      disabled: gtDenied,
      tooltip: gtDenied ? GT_CONFIRM_DENIED_TOOLTIP : undefined,
    }));
  }
  if (group === OrderStatusGroup.AWAITING_PACKING && canFulfill(ctx.roles)) {
    transition.push(entry('advance-packing', 'Đang đóng gói', 'primary', ICONS.advancePacking, one));
  }
  if (
    group === OrderStatusGroup.AWAITING_HANDOVER &&
    canFulfill(ctx.roles) &&
    row.shipping?.shipping_partner?.name === ShippingPartnerName.MANUAL
  ) {
    transition.push(entry('mark-picked-up', 'Đã gửi hàng', 'primary', ICONS.markPickedUp, one));
  }
  if (canCancelOrder(row.state, group)) {
    // TCR-1964 — khoá theo trạng thái: mờ + lý do thay vì ẩn.
    const locked = isOrderLockedForUser(group, ctx.canOverrideLock);
    transition.push(entry('cancel', 'Huỷ', 'danger', ICONS.cancel, {
      ...one,
      disabled: locked,
      tooltip: locked ? ORDER_STATUS_LOCK_TOOLTIP : undefined,
    }));
  }

  const terminal = isTerminalOrder(row);
  const { printPermissions } = ctx;
  const action: OrderPanelEntry[] = [entry('copy', 'Sao chép đơn hàng', 'primary', ICONS.copy)];
  if (canReturnOrder(row.state)) action.push(entry('return', 'Đổi trả hàng', 'primary', ICONS.return));
  // TCR-1094 (comment 33057) — đơn tự vận chuyển in phiếu xuất hàng, đơn có hãng in nhãn hãng; thiếu
  // điều kiện (hãng thật chưa có vận đơn) thì mờ + lý do thay vì gọi nhầm API.
  if (group != null && PRINT_SLIP_STATUS_GROUPS.includes(group) && printPermissions.canPrintSlip) {
    const mode = resolvePrintSlipMode(row);
    action.push(entry('print:slip', 'In phiếu', 'primary', ICONS.print, {
      disabled: mode.kind === 'unavailable',
      tooltip: mode.kind === 'unavailable' ? mode.reason : undefined,
    }));
  }
  if (!terminal) {
    if (printPermissions.canPrintReceipt) action.push(entry('print:receipt', 'In hoá đơn POS', 'primary', ICONS.print, one));
    if (printPermissions.canPrintInvoice) {
      action.push(entry('print:invoice', 'In hoá đơn bán hàng', 'primary', ICONS.print, one));
      // TCR-1962 — tải tài liệu "Đơn hàng" gửi khách, chung quyền `orders.print.invoice`.
      action.push(entry('send:customer', 'Gửi cho khách', 'primary', ICONS.send));
    }
  }
  action.push(entry('tags', 'Gán thẻ', 'primary', ICONS.tags, one));
  if (!terminal) {
    action.push(row.skipped
      ? entry('unskip', 'Bỏ đánh dấu bỏ qua', 'primary', ICONS.unskip, one)
      : entry('skip', 'Bỏ qua đơn', 'warning', ICONS.skip, one));
  }

  return { transition, action };
}

/** Panel khi chọn từ 2 đơn — số đơn đủ điều kiện cho từng hành động. */
export function buildMultiOrderSections(
  rows: TenantOrder[],
  plans: BatchOrderPlans,
  extras: RowMenuExtraPlans,
  ctx: OrderPanelContext,
): OrderPanelSections {
  const singleOnly = { disabled: true, tooltip: SINGLE_ORDER_ONLY };
  const transition: OrderPanelEntry[] = [];
  if (plans.submitDraft) transition.push(planned('submit-draft', 'Gửi xác nhận', 'primary', ICONS.submitDraft, plans.submitDraft));
  if (extras.draft) transition.push(planned('delete-draft', 'Xoá hẳn', 'danger', ICONS.deleteDraft, extras.draft));
  if (plans.confirm) transition.push(planned('confirm', 'Xác nhận', 'primary', ICONS.confirm, plans.confirm));
  if (plans.advancePacking && canFulfill(ctx.roles)) {
    transition.push(planned('advance-packing', 'Đang đóng gói', 'primary', ICONS.advancePacking, plans.advancePacking));
  }
  if (extras.markPickedUp && canFulfill(ctx.roles)) {
    transition.push(planned('mark-picked-up', 'Đã gửi hàng', 'primary', ICONS.markPickedUp, extras.markPickedUp));
  }
  transition.push(planned('cancel', 'Huỷ', 'danger', ICONS.cancel, plans.cancel));

  const { printPermissions } = ctx;
  const action: OrderPanelEntry[] = [entry('copy', 'Sao chép đơn hàng', 'primary', ICONS.copy, singleOnly)];
  if (rows.some((row) => canReturnOrder(row.state))) {
    action.push(entry('return', 'Đổi trả hàng', 'primary', ICONS.return, singleOnly));
  }
  if (printPermissions.canPrintSlip && rows.some((row) => row.status_group != null && PRINT_SLIP_STATUS_GROUPS.includes(row.status_group))) {
    action.push(entry('print:slip', 'In phiếu', 'primary', ICONS.print, singleOnly));
  }
  if (printPermissions.canPrintReceipt) action.push(planned('print:receipt', 'In hoá đơn POS', 'primary', ICONS.print, extras.print));
  if (printPermissions.canPrintInvoice) {
    action.push(planned('print:invoice', 'In hoá đơn bán hàng', 'primary', ICONS.print, extras.print));
    // Mỗi đơn chọn định dạng tải riêng nên không gộp được.
    action.push(entry('send:customer', 'Gửi cho khách', 'primary', ICONS.send, singleOnly));
  }
  // Thiếu quyền gán thẻ thì kế hoạch là null — vẫn hiện số đơn, bấm sẽ báo thiếu quyền (TCR-1714).
  action.push(plans.tags
    ? planned('tags', 'Gán thẻ', 'primary', ICONS.tags, plans.tags)
    : entry('tags', 'Gán thẻ', 'primary', ICONS.tags, { count: rows.length }));
  action.push(planned('skip', 'Bỏ qua đơn', 'warning', ICONS.skip, plans.skip));
  if (extras.unskip.eligible.length > 0) {
    action.push(planned('unskip', 'Bỏ đánh dấu bỏ qua', 'primary', ICONS.unskip, extras.unskip));
  }

  return { transition, action };
}
